import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { TableCardModelV2, TablePlayerModelV2 } from 'shared';
import { LAYOUT } from '../../../ui/layout';
import { computeSeats } from './seat-layout';
import { TrickCard } from './TrickCard';
import { TRICK_HOLD_MS } from './trick-motion';

/*
 * TrickArea — the cards played to the current trick. Each card is placed at the
 * trick slot of the seat that played it (card.playerId → relative seat index),
 * so it scales to 4/6/8 players. If playerId is missing (older payloads), it
 * falls back to a centered row by play order.
 *
 * Motion lives in TrickCard (slide-in from the hand, sweep to the winner). This
 * component just resolves positions and, once the trick is won, holds a short
 * beat so the winner is visible before triggering the sweep.
 */

/** Horizontal spacing between cards in the centered fallback, as a card-width fraction. */
const FALLBACK_SPACING = 0.7;

interface TrickAreaProps {
  tableCards: TableCardModelV2[];
  tablePlayers: (TablePlayerModelV2 | undefined)[];
  /** True while the trick is complete and being scored (ScoringTrick). */
  scoring?: boolean;
}

export const TrickArea: React.FC<TrickAreaProps> = ({
  tableCards,
  tablePlayers,
  scoring = false,
}) => {
  const seats = useMemo(() => computeSeats(tablePlayers.length), [tablePlayers.length]);
  const { card, table } = LAYOUT;

  // Once the trick is being scored, wait a beat (so the winning card and its
  // highlight register) and then let the pile sweep to the winner.
  const [sweep, setSweep] = useState(false);
  useEffect(() => {
    if (!scoring) {
      setSweep(false);
      return;
    }
    const id = window.setTimeout(() => setSweep(true), TRICK_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [scoring]);

  // The winner's seat is where the pile sweeps to.
  const winnerCard = tableCards.find((c) => c.isWinner);
  const winnerSeatIndex = winnerCard?.playerId
    ? tablePlayers.findIndex((p) => p?.id === winnerCard.playerId)
    : -1;
  const sweepTo = winnerSeatIndex >= 0 ? seats[winnerSeatIndex]?.avatar : undefined;

  return (
    <>
      {tableCards.map((played, i) => {
        const seatIndex = played.playerId
          ? tablePlayers.findIndex((p) => p?.id === played.playerId)
          : -1;
        const slot = seatIndex >= 0 ? seats[seatIndex]?.trick : undefined;

        const fallbackX =
          table.cx + (i - (tableCards.length - 1) / 2) * (card.w * FALLBACK_SPACING);
        const rest = {
          x: slot?.x ?? fallbackX,
          y: slot?.y ?? table.cy,
        };
        // Fly in from the seat's hand; fall back to the resting slot (no slide)
        // when we can't resolve the seat.
        const hand = seatIndex >= 0 ? seats[seatIndex]?.hand : undefined;
        const origin = hand ? { x: hand.x, y: hand.y } : rest;

        return (
          <TrickCard
            key={`${played.imageUrl}-${i}`}
            code={played.imageUrl}
            isWinner={played.isWinner}
            rest={rest}
            origin={origin}
            sweepTo={sweepTo}
            sweeping={sweep}
            cardW={card.w}
            cardH={card.h}
          />
        );
      })}
    </>
  );
};

export default TrickArea;
